"use client";

import React, {
	ComponentPropsWithRef,
	useCallback,
	useEffect,
	useState,
} from "react";
import type { UseEmblaCarouselType } from "embla-carousel-react";

import { Chevron } from "@/components/Atoms/Vectors";

type EmblaCarouselType = NonNullable<UseEmblaCarouselType[1]>;
import styles from "../related-embla.module.scss";

type UsePrevNextButtonsType = {
	prevArrowVisible: boolean;
	nextArrowVisible: boolean;
	onPrevButtonClick: () => void;
	onNextButtonClick: () => void;
};

function emblaNeedsScroll(api: EmblaCarouselType): boolean {
	const snaps = api.scrollSnapList();
	if (snaps.length <= 1) return false;

	const container = api.containerNode();
	const viewport = api.rootNode();
	return container.scrollWidth > viewport.clientWidth + 1;
}

export const usePrevNextButtons = (
	emblaApi: EmblaCarouselType | undefined
): UsePrevNextButtonsType => {
	const [prevArrowVisible, setPrevArrowVisible] = useState(false);
	const [nextArrowVisible, setNextArrowVisible] = useState(false);

	const onPrevButtonClick = useCallback(() => {
		emblaApi?.scrollPrev();
	}, [emblaApi]);

	const onNextButtonClick = useCallback(() => {
		emblaApi?.scrollNext();
	}, [emblaApi]);

	const update = useCallback((api: EmblaCarouselType) => {
		const needsScroll = emblaNeedsScroll(api);
		const lastIndex = api.scrollSnapList().length - 1;
		const index = api.selectedScrollSnap();

		if (!needsScroll || lastIndex < 1) {
			setPrevArrowVisible(false);
			setNextArrowVisible(false);
			return;
		}

		setPrevArrowVisible(index > 0);
		setNextArrowVisible(index < lastIndex);
	}, []);

	useEffect(() => {
		if (!emblaApi) return;

		update(emblaApi);
		emblaApi.on("reInit", update).on("select", update).on("resize", update);

		return () => {
			emblaApi.off("reInit", update);
			emblaApi.off("select", update);
			emblaApi.off("resize", update);
		};
	}, [emblaApi, update]);

	return {
		prevArrowVisible,
		nextArrowVisible,
		onPrevButtonClick,
		onNextButtonClick,
	};
};

type NavButtonProps = ComponentPropsWithRef<"button"> & {
	navColor?: { hex?: string };
	useCustomMarker?: boolean;
	markerStyle?: React.CSSProperties;
	direction?: "prev" | "next";
};

export const PrevButton: React.FC<NavButtonProps> = (props) => {
	const {
		navColor,
		useCustomMarker,
		markerStyle,
		className,
		children,
		...rest
	} = props;

	return (
		<button
			type="button"
			className={`${styles.emblaButton} ${className ?? ""}`}
			{...rest}
		>
			{children ?? (
				<span className={styles.emblaButtonIconPrev}>
					{useCustomMarker && markerStyle ? (
						<div style={markerStyle} className={styles.emblaMarker} />
					) : (
						<Chevron color={navColor} />
					)}
				</span>
			)}
			<span className="sr-only">Previous product</span>
		</button>
	);
};

export const NextButton: React.FC<NavButtonProps> = (props) => {
	const {
		navColor,
		useCustomMarker,
		markerStyle,
		className,
		children,
		...rest
	} = props;

	return (
		<button
			type="button"
			className={`${styles.emblaButton} ${className ?? ""}`}
			{...rest}
		>
			{children ?? (
				<span className={styles.emblaButtonIconNext}>
					{useCustomMarker && markerStyle ? (
						<div style={markerStyle} className={styles.emblaMarker} />
					) : (
						<Chevron color={navColor} />
					)}
				</span>
			)}
			<span className="sr-only">Next product</span>
		</button>
	);
};
